import React, { useState, useEffect, useRef } from "react";
import api from "../api"; 
import { toast } from "react-toastify";

// --- REUSABLE SEARCHABLE DROPDOWN COMPONENT (Inline) ---
const SearchableUserSelect = ({ label, value, onChange, placeholder }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedName, setSelectedName] = useState("");
    const wrapperRef = useRef(null);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSearch = async (searchTerm) => {
        setQuery(searchTerm);
        if (searchTerm.length < 2) {
            setResults([]);
            return;
        }
        try {
            const res = await api.get(`/vres/projects/users/search?query=${searchTerm}`);
            setResults(res.data);
            setIsOpen(true);
        } catch (error) {
            console.error("Search failed", error);
        }
    };

    const handleSelect = (user) => {
        setSelectedName(`${user.name} (${user.email})`);
        setQuery(`${user.name} (${user.email})`);
        onChange(user.email); // Passing Email back to parent
        setIsOpen(false);
    };

    return (
        <div style={{ marginBottom: '15px', position: 'relative' }} ref={wrapperRef}>
            <label style={styles.formLabel}>{label}</label>
            <input
                type="text"
                className="ip-input"
                style={styles.formControl}
                placeholder={placeholder}
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => query.length >= 2 && setIsOpen(true)}
            />
            {isOpen && results.length > 0 && (
                <ul style={styles.dropdownList}>
                    {results.map((user) => (
                        <li 
                            key={user.id} 
                            style={styles.dropdownItem}
                            onClick={() => handleSelect(user)}
                        >
                            <strong>{user.name}</strong> <br/>
                            <small>{user.email}</small>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const InitiateProjectPage = () => {
  const [projectTitle, setProjectTitle] = useState("");
  const [coordinatorEmail, setCoordinatorEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = () => {
    setProjectTitle("");
    setCoordinatorEmail("");
    window.location.reload(); // Simple reset for dropdown state
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!coordinatorEmail) {
        toast.warn("Please search and select a Project Coordinator");
        return;
    }

    setIsLoading(true);
    
    // Payload matches Backend ProjectInitiationRequest
    const payload = { 
        title: projectTitle, 
        email: coordinatorEmail 
    };

    try {
      await api.post("/vres/projects/initiate", payload);
      toast.success(`Project "${projectTitle}" initiated successfully!`);
      handleCancel();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to initiate project.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // --- STYLES INJECTION ---
  const PageStyleBlock = () => (
    <style>{`
      .ip-input:focus {
          border-color: #20c997 !important;
          box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.1) !important;
          outline: none;
      }
      .ip-btn:hover:not(:disabled) { opacity: 0.9; }
      .ip-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    `}</style>
  );

  return (
    <div style={styles.formWrapper}>
      <PageStyleBlock />
      
      <div style={styles.formCard}>
        <h2 style={styles.formTitle}>Initiate Project</h2>

        <form onSubmit={handleSubmit}>
          {/* Project Title */}
          <div style={styles.formGroup}>
            <label htmlFor="title" style={styles.formLabel}>Project Title</label>
            <input
              type="text"
              id="title"
              className="ip-input"
              style={styles.formControl}
              placeholder="Enter Project Title"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              required
            />
          </div>

          {/* Searchable Coordinator Dropdown */}
          <SearchableUserSelect 
            label="Project Coordinator"
            value={coordinatorEmail}
            onChange={setCoordinatorEmail}
            placeholder="Search by Name or Email..."
          />

          {/* Buttons */}
          <div style={styles.btnGroup}>
            <button
              type="button"
              onClick={handleCancel}
              className="ip-btn"
              style={{ ...styles.btn, ...styles.btnSecondary }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="ip-btn"
              style={{ ...styles.btn, ...styles.btnPrimary }}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- STYLES ---
const styles = {
  formWrapper: {
    padding: '20px',
    paddingTop: '60px', 
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center', 
    width: '100%',
    minHeight: 'calc(100vh - 70px)', 
    boxSizing: 'border-box',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
    backgroundColor: '#f0fdf9', 
  },
  formCard: {
    background: 'white',
    width: '100%',
    maxWidth: '450px',
    padding: '30px', 
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    borderTop: '5px solid #20c997', 
    margin: '0 auto',
  },
  formTitle: {
    textAlign: 'center',
    marginBottom: '20px',
    fontSize: '1.3rem',
    color: '#007bff', 
    fontWeight: '700',
    marginTop: 0,
  },
  formGroup: {
    marginBottom: '15px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: '#2d3436', 
    fontSize: '0.85rem',
  },
  formControl: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #dcdcdc',
    borderRadius: '6px',
    fontSize: '0.9rem',
    transition: 'all 0.3s',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#2d3436',
    backgroundColor: '#fff',
  },
  btnGroup: {
    display: 'flex',
    gap: '15px',
    marginTop: '25px',
    justifyContent: 'center', 
  },
  btn: {
    padding: '8px 24px',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    minWidth: '100px',
    transition: 'opacity 0.2s',
  },
  btnPrimary: {
    backgroundColor: '#007bff', 
    color: 'white',
  },
  btnSecondary: {
    backgroundColor: '#6c757d', 
    color: 'white',
  },
  dropdownList: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      border: '1px solid #dcdcdc',
      borderRadius: '0 0 6px 6px',
      backgroundColor: 'white',
      zIndex: 1000,
      maxHeight: '200px',
      overflowY: 'auto',
      listStyle: 'none',
      padding: 0,
      margin: 0,
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  dropdownItem: {
      padding: '10px',
      cursor: 'pointer',
      borderBottom: '1px solid #eee',
      fontSize: '0.9rem'
  }
};

export default InitiateProjectPage;